import React, { useCallback, useEffect, useState } from 'react';
import { useApi } from '../../../lib/api';
import PartnersSubNav from './PartnersSubNav';
import DonutChart from '../components/DonutChart';
import '../../../styles/workspace.css';
import '../../../styles/PartnerConsole.css';
import Pagination from '../../../components/shared/Pagination';
import { SearchableSelect } from '../../../components/workspace/filters/FilterDropdowns';
import ReasonModal from '../../../components/shared/ReasonModal';

const PAGE_SIZE = 10;
const REVOCABLE = ['unlinked', 'pending', 'active'];

const STATUS_LABELS = {
  unlinked: 'Waiting for signup',
  pending: 'Pending approval',
  active: 'Active',
  declined: 'Declined',
  removed: 'Removed by client',
  revoked: 'Revoked',
};

const STATUS_COLORS = {
  unlinked: { bg: '#f3f4f6', fg: '#6b7280' },
  pending:  { bg: '#fff4e5', fg: '#b45309' },
  active:   { bg: '#eafaf0', fg: '#16a34a' },
  declined: { bg: '#fce7f3', fg: '#be185d' },
  removed:  { bg: '#f3e8ff', fg: '#7e22ce' },
  revoked:  { bg: '#fdecea', fg: '#b42318' },
};

const DONUT_COLORS = {
  unlinked: '#9ca3af',
  pending: '#f97316',
  active: '#22c55e',
  declined: '#ec4899',
  removed: '#a855f7',
  revoked: '#ef4444',
};

function StatusBadge({ status }) {
  const c = STATUS_COLORS[status] || STATUS_COLORS.unlinked;
  return (
    <span className="pc-pill" style={{ background: c.bg, color: c.fg }}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

export default function PartnerLinksAuditPage() {
  const api = useApi();
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [revokeId, setRevokeId] = useState(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const res = await api.adminListPartnerClientLinks(statusFilter ? { status: statusFilter } : undefined);
      setItems(res.items || []);
      if (res.counts) setCounts(res.counts);
      setPage(1);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [api, statusFilter]);

  useEffect(() => { refresh(); }, [refresh]);

  const handleRevoke = async (reason) => {
    setBusy(true);
    try {
      await api.adminRevokeClientLink(revokeId, reason);
      setRevokeId(null);
      refresh();
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setBusy(false);
    }
  };

  const q = search.trim().toLowerCase();
  const filteredItems = q
    ? items.filter((it) =>
        [it.partner_company_name, it.client_email, it.client_name].some((v) => (v || '').toLowerCase().includes(q)))
    : items;
  const pagedItems = filteredItems.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  return (
    <div>
      <div className="admin-main-header">
        <div>
          <h1 className="admin-page-title">Client Link Audit</h1>
          <p className="admin-page-subtitle">Full lifecycle history of every partner-client relationship.</p>
        </div>
      </div>
      <PartnersSubNav />

      {Object.keys(counts).length > 0 && (() => {
        const total = Object.values(counts).reduce((s, n) => s + n, 0);
        const slices = Object.entries(counts).map(([status, value]) => ({
          label: STATUS_LABELS[status] || status,
          value,
          color: DONUT_COLORS[status] || '#9ca3af',
        }));
        return (
          <div className="admin-card">
            <h2 style={{ marginBottom: 16 }}>All client links by status ({total})</h2>
            <div className="admin-donut-wrap">
              <DonutChart slices={slices} size={110} />
              <div className="admin-donut-legend">
                {slices.map((sl) => {
                  const pct = Math.round((sl.value / total) * 100);
                  return (
                    <div key={sl.label} className="admin-donut-legend-item">
                      <div className="admin-donut-legend-label">
                        <div className="admin-donut-dot" style={{ background: sl.color }} />
                        {sl.label}
                      </div>
                      <div>
                        <span className="admin-donut-value">{sl.value}</span>
                        <span className="admin-donut-pct">({pct}%)</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        );
      })()}

      <div className="admin-card-toolbar" style={{ justifyContent: 'flex-end', gap: 8, flexWrap: 'wrap' }}>
        <input
          className="form-input"
          placeholder="Search partner or client…"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          style={{ width: 260, maxWidth: '100%' }}
        />
        <SearchableSelect
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); setPage(1); }}
          options={[
            { value: '', label: 'All statuses' },
            ...Object.entries(STATUS_LABELS).map(([value, label]) => ({ value, label })),
          ]}
          showAll={false}
          btnClass="form-select-btn"
          wrapStyle={{ width: 220 }}
          matchTriggerWidth
        />
      </div>

      {error && <div className="admin-banner error">{error}</div>}
      {loading ? (
        <p className="muted">Loading…</p>
      ) : filteredItems.length === 0 ? (
        <p className="muted">No client links{statusFilter ? ` with status "${STATUS_LABELS[statusFilter] || statusFilter}"` : ''}{q ? ` matching "${search.trim()}"` : ''}.</p>
      ) : (
        <>
          <div className="entity-table-wrap">
            <table className="entity-table" style={{ width: '100%', minWidth: 900 }}>
              <colgroup>
                <col style={{ width: '16%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: 190 }} />
                <col />
                <col style={{ width: 180 }} />
                <col style={{ width: 90 }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Partner</th>
                  <th>Client email</th>
                  <th>Status</th>
                  <th>Reason / notes</th>
                  <th>Updated</th>
                  <th className="row-actions">Action</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map((it) => (
                  <tr key={it.id}>
                    <td style={{ overflowWrap: 'anywhere' }}>{it.partner_company_name}</td>
                    <td style={{ overflowWrap: 'anywhere' }}>
                      {it.client_name && <div>{it.client_name}</div>}
                      <div className={it.client_name ? 'muted' : undefined} style={it.client_name ? { fontSize: 12 } : undefined}>{it.client_email}</div>
                    </td>
                    <td><StatusBadge status={it.status} /></td>
                    <td style={{ overflowWrap: 'anywhere' }}>{it.removal_reason || '—'}</td>
                    <td>{new Date(it.updated_at).toLocaleString()}</td>
                    <td className="row-actions">
                      {REVOCABLE.includes(it.status) ? (
                        <button className="btn-link-sm" onClick={() => setRevokeId(it.id)} disabled={busy}>Revoke</button>
                      ) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <Pagination page={page} totalItems={filteredItems.length} pageSize={PAGE_SIZE} onPageChange={setPage} />
        </>
      )}

      {revokeId != null && (
        <ReasonModal
          title="Revoke client link"
          label="Reason for revoking"
          confirmLabel="Revoke"
          busy={busy}
          onConfirm={handleRevoke}
          onClose={() => setRevokeId(null)}
        />
      )}
    </div>
  );
}
